import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import {TicketCheck, Plus, Clock, CheckCircle2, AlertTriangle, BarChart3, ArrowRight, Loader2, LogOut, Calendar, Phone, Video, Shield, Settings, MessageCircleDashed as MessageCircle} from 'lucide-react'

const typeIcons: Record<string, any> = {
  "Appel découverte": Phone,
  "Rendez-vous technique": Settings,
  "Audit IT": Shield,
  "Assistance planifiée": Video,
}

const rdvStatusColor: Record<string, string> = {
  nouveau: "text-cyan-400 bg-cyan-500/10",
  confirme: "text-emerald-400 bg-emerald-500/10",
  termine: "text-slate-400 bg-slate-500/10",
  annule: "text-red-400 bg-red-500/10",
}

const rdvStatusLabels: Record<string, string> = {
  nouveau: "En attente",
  confirme: "Confirmé",
  termine: "Terminé",
  annule: "Annulé",
}

export default function ClientDashboard() {
  const { user } = useAuth()
  const [tickets, setTickets] = useState<any[]>([])
  const [appointments, setAppointments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<"tickets" | "rdv">("tickets")

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [ticketsRes, rdvRes] = await Promise.all([
        lumi.entities.tickets.list({ sort: { createdAt: -1 }, limit: 100 }),
        lumi.entities.appointments.list({ sort: { createdAt: -1 }, limit: 100 }),
      ])
      setTickets(ticketsRes.list)
      setAppointments(rdvRes.list)
    } catch (err) {
      console.error("Erreur chargement données:", err)
    } finally {
      setLoading(false)
    }
  }

  const statusCounts = {
    nouveau: tickets.filter((t) => t.status === "nouveau").length,
    en_cours: tickets.filter((t) => ["en_cours", "en_attente"].includes(t.status)).length,
    resolu: tickets.filter((t) => ["resolu", "ferme"].includes(t.status)).length,
    total: tickets.length,
  }

  const rdvCounts = {
    total: appointments.length,
    en_attente: appointments.filter((r) => r.status === "nouveau").length,
    confirme: appointments.filter((r) => r.status === "confirme").length,
  }

  const priorityColor: Record<string, string> = {
    faible: "text-slate-400 bg-slate-500/10",
    normale: "text-blue-400 bg-blue-500/10",
    haute: "text-amber-400 bg-amber-500/10",
    urgente: "text-red-400 bg-red-500/10",
  }

  const statusColor: Record<string, string> = {
    nouveau: "text-cyan-400 bg-cyan-500/10",
    en_attente: "text-amber-400 bg-amber-500/10",
    en_cours: "text-blue-400 bg-blue-500/10",
    attente_client: "text-orange-400 bg-orange-500/10",
    resolu: "text-emerald-400 bg-emerald-500/10",
    ferme: "text-slate-400 bg-slate-500/10",
    escalade: "text-red-400 bg-red-500/10",
    planifie: "text-purple-400 bg-purple-500/10",
    annule: "text-slate-500 bg-slate-600/10",
  }

  const statusLabels: Record<string, string> = {
    nouveau: "Nouveau",
    en_attente: "En attente",
    en_cours: "En cours",
    attente_client: "Attente client",
    resolu: "Résolu",
    ferme: "Fermé",
    escalade: "Escaladé",
    planifie: "Planifié",
    annule: "Annulé",
  }

  const handleLogout = async () => {
    await lumi.auth.signOut()
    window.location.href = "/"
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 animate-fade-in-up">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">
              Bonjour, <span className="gradient-text">{user?.userName || "Client"}</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1">Bienvenue dans votre espace client LJJ IT</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/espace-client/nouveau-ticket" className="btn-primary text-sm !py-2.5">
              <Plus className="w-4 h-4" />
              Nouveau ticket
            </Link>
            <Link to="/rendez-vous" className="btn-secondary text-sm !py-2.5">
              <Calendar className="w-4 h-4" />
              Prendre RDV
            </Link>
            <button onClick={handleLogout} className="btn-secondary text-sm !py-2.5">
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>

        {/* Bandeau WhatsApp urgence */}
        <a
          href="https://wa.me/41792489590"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 mb-8 p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors group animate-fade-in-up"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
            <MessageCircle className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-300">Besoin urgent ?</p>
            <p className="text-xs text-slate-400">Contactez-nous directement via WhatsApp pour une assistance immédiate</p>
          </div>
          <ArrowRight className="w-4 h-4 text-emerald-400 group-hover:translate-x-1 transition-transform shrink-0" />
        </a>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: BarChart3, label: "Total tickets", value: statusCounts.total, color: "from-cyan-500 to-blue-500" },
            { icon: AlertTriangle, label: "Nouveaux", value: statusCounts.nouveau, color: "from-amber-500 to-orange-500" },
            { icon: Calendar, label: "Rendez-vous", value: rdvCounts.total, color: "from-violet-500 to-purple-500" },
            { icon: CheckCircle2, label: "RDV confirmés", value: rdvCounts.confirme, color: "from-emerald-500 to-teal-500" },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className="card !p-5 animate-fade-in-up"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-slate-400 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 p-1 rounded-xl bg-slate-800/50 border border-slate-700/40 w-fit animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
          <button
            onClick={() => setActiveTab("tickets")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === "tickets"
                ? "bg-cyan-500/15 text-cyan-300 shadow-sm"
                : "text-slate-400 hover:text-white hover:bg-slate-700/40"
            }`}
          >
            <TicketCheck className="w-4 h-4" />
            Mes tickets
            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
              activeTab === "tickets" ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-700 text-slate-500"
            }`}>
              {tickets.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("rdv")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === "rdv"
                ? "bg-violet-500/15 text-violet-300 shadow-sm"
                : "text-slate-400 hover:text-white hover:bg-slate-700/40"
            }`}
          >
            <Calendar className="w-4 h-4" />
            Mes rendez-vous
            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
              activeTab === "rdv" ? "bg-violet-500/20 text-violet-300" : "bg-slate-700 text-slate-500"
            }`}>
              {appointments.length}
            </span>
          </button>
        </div>

        {/* Content */}
        <div className="animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          ) : activeTab === "tickets" ? (
            /* ─── TICKETS ─── */
            tickets.length === 0 ? (
              <div className="card !p-12 text-center">
                <TicketCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Aucun ticket</h3>
                <p className="text-sm text-slate-400 mb-6">Vous n'avez pas encore créé de ticket de support.</p>
                <Link to="/espace-client/nouveau-ticket" className="btn-primary text-sm">
                  <Plus className="w-4 h-4" />
                  Créer mon premier ticket
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <Link
                    key={ticket._id}
                    to={`/espace-client/ticket/${ticket._id}`}
                    className="card !p-5 flex flex-col sm:flex-row sm:items-center gap-4 group hover:border-cyan-500/40"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor[ticket.status] || "text-slate-400 bg-slate-500/10"}`}>
                          {statusLabels[ticket.status] || ticket.status}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColor[ticket.priority] || ""}`}>
                          {ticket.priority}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-white truncate group-hover:text-cyan-400 transition-colors">
                        {ticket.subject}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {ticket.category?.replace(/_/g, " ")} &middot; {new Date(ticket.createdAt).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            )
          ) : (
            /* ─── RENDEZ-VOUS ─── */
            appointments.length === 0 ? (
              <div className="card !p-12 text-center">
                <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Aucun rendez-vous</h3>
                <p className="text-sm text-slate-400 mb-6">Vous n'avez pas encore demandé de rendez-vous.</p>
                <Link to="/rendez-vous" className="btn-primary text-sm">
                  <Calendar className="w-4 h-4" />
                  Planifier un rendez-vous
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.map((rdv) => {
                  const Icon = typeIcons[rdv.type] || Calendar
                  return (
                    <div
                      key={rdv._id}
                      className="card !p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                    >
                      <div className="w-11 h-11 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-violet-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${rdvStatusColor[rdv.status] || "text-slate-400 bg-slate-500/10"}`}>
                            {rdvStatusLabels[rdv.status] || rdv.status}
                          </span>
                          <span className="text-xs text-slate-500">{rdv.type}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-white font-medium">
                            {formatDate(rdv.datePreferee)}
                          </span>
                          {rdv.heurePreferee && (
                            <span className="flex items-center gap-1 text-slate-400">
                              <Clock className="w-3.5 h-3.5" />
                              {rdv.heurePreferee?.replace(":", "h")}
                            </span>
                          )}
                        </div>
                        {rdv.details && (
                          <p className="text-xs text-slate-500 mt-1 truncate">{rdv.details}</p>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 shrink-0">
                        Demandé le {new Date(rdv.createdAt).toLocaleDateString("fr-FR")}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}
